import React, { useEffect, useState } from 'react';
import RecipeCard from './RecipeCard';

import { API_BASE_URL } from '../config';
import { calculateRecipeCostDisplay, resolveRecipeScalingContext } from '../utils/recipePricing';
import { resolveMatchDisplay } from '../utils/matchDisplay';

const groupCostHydrationCache = new Map();

const getCompactPriceSource = (item) => String(item?.priceSnapshot?.source || '').trim().toUpperCase();
const hasSnapshotBreakdown = (item) => Array.isArray(item?.priceSnapshot?.breakdown) && item.priceSnapshot.breakdown.length > 0;
const shouldHydrateRecipeCost = (item, displayedCostTotal) => {
    const compactPriceSource = getCompactPriceSource(item);
    return (
        item?.sourceOfTruth === 'library_catalog' ||
        compactPriceSource === 'LIBRARY_CATALOG' ||
        compactPriceSource === 'SMART_SEARCH_DISH_FALLBACK' ||
        compactPriceSource === 'FALLBACK' ||
        !hasSnapshotBreakdown(item) ||
        Number(displayedCostTotal || 0) <= 0
    );
};

const buildDetailPath = (item, portion) => {
    const numericRecipeId = Number(item?.id);
    const safePortion = Number(portion) || 1;
    if (item?.sourceOfTruth === 'library_catalog') {
        return `/api/recipes/library/${numericRecipeId}?portion=${safePortion}`;
    }
    return `/api/recipes/${numericRecipeId}?portion=${safePortion}`;
};

const resolveEffectiveDisplayedCost = (item, displayedCostTotal, hydratedCostMap) => {
    const hydrated = Number(hydratedCostMap?.[item?.id] || 0);
    if (hydrated > 0) return hydrated;
    const compact = Number(displayedCostTotal || 0);
    return compact > 0 ? compact : 0;
};

const detectMealType = (recipe) => {
    const tokens = [
        recipe?.serveStyle,
        recipe?.category,
        recipe?.mealType,
        recipe?.dishType,
        recipe?.recipeType,
        recipe?.name,
        recipe?.title
    ]
        .map((x) => String(x || '').toLocaleLowerCase('tr-TR'))
        .join(' ');
    if (/(corba|çorba|soup|sulu)/.test(tokens)) return 'soup';
    if (/(salata|salad|meze)/.test(tokens)) return 'salad';
    if (/(ana yemek|anayemek|main|yemek|et|tavuk)/.test(tokens)) return 'main';
    return 'other';
};

const outsideMultiplierByType = {
    soup: 2.2,
    main: 2.9,
    salad: 2.0,
    other: 2.5
};

const calculateOutsideCost = (recipe, homeCost) => {
    const mealType = detectMealType(recipe);
    const inferredOutsideFallback = homeCost * (outsideMultiplierByType[mealType] || outsideMultiplierByType.other);
    const inferredOutsideCost = Number(
        recipe?.priceSnapshot?.outsidePrice ??
        recipe?.presentation?.outsidePrice ??
        inferredOutsideFallback
    ) || 0;
    return Math.round(inferredOutsideCost);
};

const calculateSavings = (recipe, homeCost) => {
    const inferredOutsideCost = calculateOutsideCost(recipe, homeCost);
    const pocketSaving = Math.max(0, inferredOutsideCost - homeCost);
    return Math.round(pocketSaving);
};

/**
 * RecipeMenuGroup
 * Standardized component to display a main recipe along with its suggested pairings (side dishes).
 * Used across SmartSearch, Athlete Kitchen, Diet Kitchen, and Daily Menu.
 */
const RecipeMenuGroup = ({
    recipe,
    onCardClick,
    onSwap = null,
    selectedRecipeNames = [],
    onToggleSelect = null,
    hideActionButtons = false,
    isDarkMode = false,
    isModern = false,
    isCompleted = false,
    layoutMode = 'featured', // 'featured' (recommendation) or 'compact' (browse)
    userPortion = 4,
    highlightAdaptive = false,
    ...props
}) => {
    const interactionDisabled = Boolean(isCompleted || props.interactionDisabled);
    const completionContainerClass = interactionDisabled ? 'pointer-events-none opacity-60 grayscale-[0.45] saturate-50' : '';
    const safeOnCardClick = interactionDisabled ? null : onCardClick;
    const safeOnToggleSelect = (interactionDisabled || hideActionButtons) ? null : onToggleSelect;
    const hasPairings = recipe.suggestedPairings && Array.isArray(recipe.suggestedPairings) && recipe.suggestedPairings.length > 0;
    const mainDisplayCost = calculateRecipeCostDisplay(recipe, userPortion);
    const [hydratedCostMap, setHydratedCostMap] = useState({});
    const isAdaptiveMain = highlightAdaptive && !!(recipe.isAdapted || recipe.facts?.isAdapted || recipe.presentation?.isAdapted);

    // Selection Logic: A group is considered "selected" if the main recipe is selected
    const isMainSelected = selectedRecipeNames.includes(recipe.name || recipe.title);
    const areAllPairsSelected = hasPairings && recipe.suggestedPairings.every(p => selectedRecipeNames.includes(p.name || p.title));
    const isGroupSelected = isMainSelected && areAllPairsSelected;

    const handleToggleGroup = (e) => {
        if (e) e.stopPropagation();
        if (safeOnToggleSelect) {
            safeOnToggleSelect([recipe, ...(recipe.suggestedPairings || [])]);
        }
    };

    const handleSwap = (e) => {
        e.stopPropagation();
        if (typeof onSwap === 'function') {
            onSwap(recipe);
        }
    };

    useEffect(() => {
        let cancelled = false;
        const items = [recipe, ...(Array.isArray(recipe?.suggestedPairings) ? recipe.suggestedPairings : [])];
        const token = localStorage.getItem('authToken');
        const nextHydrated = {};

        const tasks = items.map(async (item) => {
            const numericRecipeId = Number(item?.id);
            const { displayedCostTotal } = calculateRecipeCostDisplay(item, userPortion);
            if (!Number.isFinite(numericRecipeId) || !shouldHydrateRecipeCost(item, displayedCostTotal)) return;

            const cacheKey = `${numericRecipeId}:${Number(userPortion) || 1}`;
            if (groupCostHydrationCache.has(cacheKey)) {
                nextHydrated[item.id] = groupCostHydrationCache.get(cacheKey);
                return;
            }

            try {
                const detailPath = buildDetailPath(item, userPortion);
                const res = await fetch(`${API_BASE_URL}${detailPath}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });
                if (!res.ok) return;
                const full = await res.json();
                const total = Number(
                    full?.priceSnapshot?.total ??
                    full?.presentation?.totalCost ??
                    full?.totalCost ??
                    0
                );
                if (Number.isFinite(total) && total > 0) {
                    groupCostHydrationCache.set(cacheKey, total);
                    nextHydrated[item.id] = total;
                }
            } catch {
                // Silent fallback: keep compact payload cost.
            }
        });

        Promise.all(tasks).then(() => {
            if (cancelled || Object.keys(nextHydrated).length === 0) return;
            setHydratedCostMap((prev) => ({ ...prev, ...nextHydrated }));
        });

        return () => {
            cancelled = true;
        };
    }, [recipe, userPortion]);

    const mainEffectiveCost = resolveEffectiveDisplayedCost(recipe, mainDisplayCost.displayedCostTotal, hydratedCostMap);

    if (isModern && hasPairings) {

        // Helper to resolve card image
        const resolveImage = (img) => {
            if (!img) return "https://images.unsplash.com/photo-1495195129352-aec329a6d7ca?q=80&w=800&auto=format&fit=crop";
            return (img.startsWith('http') || img.startsWith('data:')) ? img : `${API_BASE_URL}${img}`;
        };

        const isFeatured = layoutMode === 'featured' || layoutMode === 'browseFeaturedPair';
        const isCompact = layoutMode === 'compact';
        const pairingSideBySide = layoutMode === 'browseFeaturedPair' || layoutMode === 'featured';

        return (
            <div className={`flex flex-col gap-4 h-full relative group w-full ${completionContainerClass}`}>
                {typeof onSwap === 'function' && (
                    <button
                        onClick={handleSwap}
                        className="absolute top-2 left-2 z-50 h-9 w-9 rounded-full bg-white/95 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:text-rose-600 hover:border-rose-300 transition-all shadow-sm flex items-center justify-center"
                        title="Değiştir"
                        aria-label="Değiştir"
                    >
                        <span className="material-symbols-outlined text-[18px]">swap_horiz</span>
                    </button>
                )}
                {/* Main Container */}
                <div className={`relative h-full p-2.5 md:p-3 pt-5 md:pt-6 rounded-3xl border-2 transition-all duration-500 flex flex-col ${isGroupSelected
                    ? 'border-emerald-500 bg-emerald-50/10 dark:bg-emerald-950/10'
                    : 'border-rose-500/10 bg-rose-50/5 dark:bg-rose-950/5'
                    }`}>

                    {/* Premium Badge */}
                    <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 shadow-xl backdrop-blur-md z-10 border ${isGroupSelected
                        ? 'bg-emerald-600 text-white border-emerald-400/30'
                        : 'bg-rose-600 text-white border-rose-400/30'
                        }`}>
                        <span className="material-symbols-outlined text-[14px]">auto_awesome</span>
                        {isGroupSelected ? 'BU MENÜ SEÇİLDİ' : 'AKILLI MENÜ ÖNERİSİ'}
                    </div>

                    {/* Group Selection Tick Box */}
                    {safeOnToggleSelect && (
                        <button
                            onClick={handleToggleGroup}
                            className={`absolute -top-3 -right-3 size-10 rounded-full shadow-2xl flex items-center justify-center transition-all z-20 active:scale-90 ${isGroupSelected
                                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                : 'bg-white dark:bg-slate-800 text-slate-400 hover:text-rose-500 hover:bg-rose-50 border-2 border-slate-100 dark:border-slate-700'
                                }`}
                        >
                            <span className="material-symbols-outlined text-[20px] font-black">
                                {isGroupSelected ? 'check' : 'add'}
                            </span>
                        </button>
                    )}

                    <div className={`${pairingSideBySide ? 'grid grid-cols-1 md:grid-cols-2 gap-4 flex-grow' : 'flex flex-col gap-4 flex-grow'}`}>

                        {/* 1. Main Dish Card */}
                        <div
                            className={`bg-white dark:bg-slate-800/50 rounded-[2rem] shadow-lg flex flex-col flex-1 overflow-hidden border transition-all duration-300 group/item ${isGroupSelected ? 'border-emerald-500/30' : 'border-slate-100 dark:border-white/5'} ${isAdaptiveMain ? 'border-amber-400 dark:border-amber-500 shadow-[0_0_0_1px_rgba(251,191,36,0.22),0_16px_40px_rgba(245,158,11,0.16)] dark:shadow-[0_0_0_1px_rgba(245,158,11,0.12)]' : ''}`}
                            onClick={() => safeOnCardClick && safeOnCardClick(recipe)}
                        >
                            <div className={`relative w-full shrink-0 overflow-hidden`}>
                                <img
                                    alt={recipe.title || recipe.name}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover/item:scale-110"
                                    src={resolveImage(recipe.imageUrl || recipe.image_url)}
                                />
                                <div className="absolute top-2 left-2 bg-black/40 backdrop-blur-md text-white px-2.5 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest border border-white/10">Ana Yemek</div>
                                {isAdaptiveMain && (
                                    <div className="absolute bottom-2 left-2 bg-amber-500/90 backdrop-blur-md text-white px-2.5 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest border border-amber-200/40 shadow-lg">
                                        Alternatifli Uyum
                                    </div>
                                )}
                            </div>
                            <div className={`${isFeatured ? 'p-5' : 'p-3'} flex flex-col flex-grow justify-center gap-2`}>
                                {isFeatured && (
                                    <div className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-wider ${isDarkMode ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                        }`}>
                                        <span className="material-symbols-outlined text-[12px]">local_fire_department</span>
                                        BUGÜN YAP
                                    </div>
                                )}
                                <div className="flex items-center justify-between gap-2">
                                    <h4 className={`${isFeatured ? 'text-base' : 'text-xs'} font-black text-slate-900 dark:text-white line-clamp-2 leading-tight flex-1`}>{recipe.title || recipe.name}</h4>

                                    <div className={`${isFeatured ? 'bg-rose-50 dark:bg-rose-950/30 px-3 py-2 border' : 'bg-transparent'} rounded-2xl shrink-0 flex items-center gap-1.5 border-rose-100 dark:border-rose-900/20`}>
                                        <span className={`${isFeatured ? 'text-xs' : 'text-[10px]'} font-black text-rose-600 dark:text-rose-400`}>
                                            {Number(mainEffectiveCost || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ₺</span>
                                    </div>
                                </div>

                                {(() => {
                                    const roundedSaving = calculateSavings(recipe, mainEffectiveCost);
                                    if (roundedSaving <= 0) return null;
                                    return (
                                        <div className="rounded-xl border px-3 py-2 bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-300">
                                            <div className="text-base font-black leading-none">+{roundedSaving} ₺ cebinde kalır</div>
                                            <div className="mt-1 text-[9px] font-semibold opacity-80">Dışarıdan yemek yerine evde yaparsan</div>
                                        </div>
                                    );
                                })()}

                                <div className="flex flex-wrap items-center gap-1.5">
                                    {/* Allergen & Health Badges (Consistency with RecipeCard) */}
                                    {((recipe.match?.isAllergenSafe === false) || recipe.isAllergenSafe === false || recipe._hasAllergenCollision) && (
                                        <div className="px-1.5 py-0.5 rounded-md flex items-center gap-1 bg-red-600 text-white shadow-sm">
                                            <span className="material-symbols-outlined text-white text-[11px] font-bold">report</span>
                                            <span className="text-[8px] font-bold uppercase tracking-tight">Alerjen</span>
                                        </div>
                                    )}
                                    {(recipe.healthBadges || recipe.health?.badges || []).slice(0, 1).map((badge, idx) => (
                                        <div key={idx} className="px-1.5 py-0.5 rounded-md flex items-center gap-1 border bg-blue-500/10 text-blue-600 border-blue-300/40">
                                            <span className="material-symbols-outlined text-[11px] font-bold">verified</span>
                                            <span className="text-[8px] font-bold tracking-tight">{badge}</span>
                                        </div>
                                    ))}

                                    {(() => {
                                        const matchDisplay = resolveMatchDisplay(recipe);
                                        const perc = matchDisplay.matchPercentage;
                                        const isFull = matchDisplay.isFull || perc >= 95;
                                        return (
                                            <div className={`px-1.5 py-0.5 rounded-md flex items-center gap-1 border ${isFull ? 'bg-emerald-500/10 text-emerald-600 border-emerald-400/20' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                                                <span className={`material-symbols-outlined text-[11px] font-bold ${isFull ? 'text-emerald-600' : 'text-slate-400'}`}>{isFull ? 'check_circle' : 'equalizer'}</span>
                                                <span className={`text-[8px] font-bold tracking-tight text-slate-600`}>%{perc} Uyum</span>
                                            </div>
                                        );
                                    })()}

                                    <div className="flex items-center gap-1 bg-slate-50 dark:bg-white/5 px-2 py-1 rounded-xl text-[10px] font-black text-slate-500 dark:text-slate-400">
                                        <span className="material-symbols-outlined text-[14px]">bolt</span>
                                        {(() => {
                                            const currentSrv = Math.max(1, userPortion);
                                            const m = resolveRecipeScalingContext(recipe, currentSrv).effectiveMultiplier;
                                            const baseCal = parseInt(recipe.calories) || 0;
                                            return Math.round(baseCal * m);
                                        })()} kcal
                                    </div>
                                    <div className="flex items-center gap-1 bg-slate-50 dark:bg-white/5 px-2 py-1 rounded-xl text-[10px] font-black text-slate-500 dark:text-slate-400">
                                        <span className="material-symbols-outlined text-[14px]">schedule</span>
                                        {(() => {
                                            const currentPortion = Number(userPortion) || 1;
                                            const scaling = resolveRecipeScalingContext(recipe, currentPortion);
                                            const fixed = parseInt(recipe.fixedTime) || 10;
                                            const variable = parseInt(recipe.variableTime) || 15;
                                            const exponent = parseFloat(recipe.scalingModel?.exponent) || 0.7;

                                            const baseForTime = scaling.isPreScaled ? scaling.appliedPortion : scaling.baseServings;
                                            const scaleFactor = (currentPortion === baseForTime) ? 1 : Math.pow(currentPortion / Math.max(1, baseForTime), exponent);
                                            return Math.max(Math.round(fixed + (variable * scaleFactor)), parseInt(recipe.cookTime || recipe.cookingTime || 25));
                                        })()} dk
                                    </div>
                                    <div className="flex items-center justify-center size-8 rounded-xl bg-emerald-500 text-white text-[10px] font-black shadow-lg shadow-emerald-500/20">
                                        {recipe.difficulty?.charAt(0).toUpperCase() || 'K'}
                                    </div>
                                    <div className="flex items-center gap-1 bg-emerald-50 dark:bg-emerald-950/30 px-2 py-1 rounded-xl text-[10px] font-black text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/20">
                                        <span className="material-symbols-outlined text-[14px]">groups</span>
                                        {userPortion} Kişilik
                                    </div>
                                </div>

                                {/* Evde vs Dışarıda Comparison */}
                                {isFeatured && (
                                    <div className="rounded-lg border border-slate-200/60 bg-slate-50/70 px-2.5 py-2 dark:border-white/10 dark:bg-white/5">
                                        <div className="flex items-center justify-between text-[11px] font-black">
                                            <span className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>Evde</span>
                                            <span className="text-rose-500">{Math.round(mainEffectiveCost)} ₺</span>
                                        </div>
                                        <div className="mt-1.5 flex items-center justify-between text-[11px] font-black">
                                            <span className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>Dışarıda</span>
                                            <span className={isDarkMode ? 'text-slate-200' : 'text-slate-700'}>~{calculateOutsideCost(recipe, mainEffectiveCost)} ₺</span>
                                        </div>
                                    </div>
                                )}

                                {/* Details Link */}
                                {!hideActionButtons && (
                                    <div className="mt-2 flex justify-end">
                                        <button
                                            className={`flex items-center gap-1 transition-colors group/detail active:scale-95 ${isDarkMode ? 'text-white/40 hover:text-white' : 'text-slate-400 hover:text-slate-800'}`}
                                            onClick={() => onCardClick && onCardClick(recipe)}
                                        >
                                            <span className="text-[10px] font-black uppercase tracking-widest">Detaylar</span>
                                            <span className="material-symbols-outlined text-[14px] font-black group-hover/detail:translate-x-0.5 transition-transform">chevron_right</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 2. Side Dish Card (Pairing) */}
                        {recipe.suggestedPairings && recipe.suggestedPairings.map((pairing, pIdx) => {
                            const pairingDisplayCost = calculateRecipeCostDisplay(pairing, userPortion);
                            const pairingEffectiveCost = resolveEffectiveDisplayedCost(pairing, pairingDisplayCost.displayedCostTotal, hydratedCostMap);
                            const isAdaptivePairing = highlightAdaptive && !!(pairing.isAdapted || pairing.facts?.isAdapted || pairing.presentation?.isAdapted);
                            return (
                                <div
                                    key={pIdx}
                                    className={`bg-white dark:bg-slate-800/50 rounded-[2rem] shadow-md flex flex-col flex-1 overflow-hidden border transition-all duration-300 group/pair ${isGroupSelected ? 'border-emerald-500/30' : 'border-slate-100 dark:border-white/5'} ${isAdaptivePairing ? 'border-amber-400 dark:border-amber-500 shadow-[0_0_0_1px_rgba(251,191,36,0.22),0_16px_40px_rgba(245,158,11,0.16)] dark:shadow-[0_0_0_1px_rgba(245,158,11,0.28),0_18px_44px_rgba(245,158,11,0.12)]' : ''}`}
                                    onClick={() => onCardClick && onCardClick(pairing)}
                                >
                                    <div className={`relative w-full shrink-0 overflow-hidden`}>
                                        <img
                                            alt={pairing.title || pairing.name}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover/pair:scale-110"
                                            src={resolveImage(pairing.imageUrl || pairing.image_url)}
                                        />
                                        <div className="absolute top-2 left-2 bg-emerald-600/80 backdrop-blur-md text-white px-2.5 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest border border-emerald-400/30 shadow-lg">Eşlikçi</div>
                                    </div>
                                    <div className={`${isFeatured ? 'p-5' : 'p-3'} flex flex-col flex-grow justify-center gap-2`}>
                                        {isFeatured && (
                                            <div className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-wider ${isDarkMode ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                }`}>
                                                <span className="material-symbols-outlined text-[12px]">local_fire_department</span>
                                                BUGÜN YAP
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between gap-2">
                                            <h4 className={`${isFeatured ? 'text-base' : 'text-xs'} font-black text-slate-900 dark:text-white line-clamp-2 leading-tight flex-1`}>{pairing.title || pairing.name}</h4>
                                            <div className={`${isFeatured ? 'bg-rose-50 dark:bg-rose-950/30 px-3 py-2 border' : 'bg-transparent'} rounded-2xl shrink-0 border-rose-100 dark:border-rose-900/20`}>
                                                <span className={`${isFeatured ? 'text-xs' : 'text-[10px]'} font-black text-rose-600 dark:text-rose-400`}>
                                                    {Number(pairingEffectiveCost || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ₺</span>
                                            </div>
                                        </div>

                                        {(() => {
                                            const roundedSaving = calculateSavings(pairing, pairingEffectiveCost);
                                            if (roundedSaving <= 0) return null;
                                            return (
                                                <div className="rounded-xl border px-3 py-2 bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-300">
                                                    <div className="text-base font-black leading-none">+{roundedSaving} ₺ cebinde kalır</div>
                                                    <div className="mt-1 text-[9px] font-semibold opacity-80">Dışarıdan yemek yerine evde yaparsan</div>
                                                </div>
                                            );
                                        })()}

                                        <div className="flex flex-wrap items-center gap-1.5">
                                            {/* Allergen & Health Badges (Consistency with RecipeCard) */}
                                            {((pairing.match?.isAllergenSafe === false) || pairing.isAllergenSafe === false || pairing._hasAllergenCollision) && (
                                                <div className="px-1.5 py-0.5 rounded-md flex items-center gap-1 bg-red-600 text-white shadow-sm">
                                                    <span className="material-symbols-outlined text-white text-[11px] font-bold">report</span>
                                                    <span className="text-[8px] font-bold uppercase tracking-tight">Alerjen</span>
                                                </div>
                                            )}
                                            {(pairing.healthBadges || pairing.health?.badges || []).slice(0, 1).map((badge, idx) => (
                                                <div key={idx} className="px-1.5 py-0.5 rounded-md flex items-center gap-1 border bg-blue-500/10 text-blue-600 border-blue-300/40">
                                                    <span className="material-symbols-outlined text-[11px] font-bold">verified</span>
                                                    <span className="text-[8px] font-bold tracking-tight">{badge}</span>
                                                </div>
                                            ))}

                                            {(() => {
                                                const matchDisplay = resolveMatchDisplay(pairing);
                                                const perc = matchDisplay.matchPercentage;
                                                const isFull = matchDisplay.isFull || perc >= 95;
                                                return (
                                                    <div className={`px-1.5 py-0.5 rounded-md flex items-center gap-1 border ${isFull ? 'bg-emerald-500/10 text-emerald-600 border-emerald-400/20' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                                                        <span className={`material-symbols-outlined text-[11px] font-bold ${isFull ? 'text-emerald-600' : 'text-slate-400'}`}>{isFull ? 'check_circle' : 'equalizer'}</span>
                                                        <span className={`text-[8px] font-bold tracking-tight text-slate-600`}>%{perc} Uyum</span>
                                                    </div>
                                                );
                                            })()}

                                            <div className="flex items-center gap-1 bg-slate-50 dark:bg-white/5 px-2 py-1 rounded-xl text-[10px] font-black text-slate-500 dark:text-slate-400">
                                                <span className="material-symbols-outlined text-[14px]">bolt</span>
                                                {(() => {
                                                    const currentSrv = Math.max(1, userPortion);
                                                    const m = resolveRecipeScalingContext(pairing, currentSrv).effectiveMultiplier;
                                                    const baseCal = parseInt(pairing.calories) || 0;
                                                    return Math.round(baseCal * m);
                                                })()} kcal
                                            </div>
                                            <div className="flex items-center gap-1 bg-slate-50 dark:bg-white/5 px-2 py-1 rounded-xl text-[10px] font-black text-slate-500 dark:text-slate-400">
                                                <span className="material-symbols-outlined text-[14px]">schedule</span>
                                                {(() => {
                                                    const currentPortion = Number(userPortion) || 1;
                                                    const scaling = resolveRecipeScalingContext(pairing, currentPortion);
                                                    const fixed = parseInt(pairing.fixedTime) || 10;
                                                    const variable = parseInt(pairing.variableTime) || 15;
                                                    const exponent = parseFloat(pairing.scalingModel?.exponent) || 0.7;

                                                    const baseForTime = scaling.isPreScaled ? scaling.appliedPortion : scaling.baseServings;
                                                    const scaleFactor = (currentPortion === baseForTime) ? 1 : Math.pow(currentPortion / Math.max(1, baseForTime), exponent);
                                                    return Math.max(Math.round(fixed + (variable * scaleFactor)), parseInt(pairing.cookTime || pairing.cookingTime || 25));
                                                })()} dk
                                            </div>
                                            <div className="flex items-center gap-1 bg-emerald-50 dark:bg-emerald-950/30 px-2 py-1 rounded-xl text-[10px] font-black text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/20">
                                                <span className="material-symbols-outlined text-[14px]">groups</span>
                                                {userPortion} Kişilik
                                            </div>
                                        </div>

                                        {/* Evde vs Dışarıda Comparison */}
                                        {isFeatured && (
                                            <div className="rounded-lg border border-slate-200/60 bg-slate-50/70 px-2.5 py-2 dark:border-white/10 dark:bg-white/5">
                                                <div className="flex items-center justify-between text-[11px] font-black">
                                                    <span className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>Evde</span>
                                                    <span className="text-rose-500">{Math.round(pairingEffectiveCost)} ₺</span>
                                                </div>
                                                <div className="mt-1.5 flex items-center justify-between text-[11px] font-black">
                                                    <span className={isDarkMode ? 'text-slate-300' : 'text-slate-600'}>Dışarıda</span>
                                                    <span className={isDarkMode ? 'text-slate-200' : 'text-slate-700'}>~{calculateOutsideCost(pairing, pairingEffectiveCost)} ₺</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Details Link */}
                                        {!hideActionButtons && (
                                            <div className="mt-2 flex justify-end">
                                                <button
                                                    className={`flex items-center gap-1 transition-colors group/detail active:scale-95 ${isDarkMode ? 'text-white/40 hover:text-white' : 'text-slate-400 hover:text-slate-800'}`}
                                                    onClick={() => onCardClick && onCardClick(pairing)}
                                                >
                                                    <span className="text-[10px] font-black uppercase tracking-widest">Detaylar</span>
                                                    <span className="material-symbols-outlined text-[14px] font-black group-hover/detail:translate-x-0.5 transition-transform">chevron_right</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                    </div>

                    {safeOnToggleSelect && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
                            <button
                                onClick={handleToggleGroup}
                                className={`pointer-events-auto min-w-[130px] px-5 py-2.5 rounded-xl font-black text-sm transition-all active:scale-95 shadow-xl flex items-center justify-center gap-2 ${
                                    isGroupSelected
                                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                        : 'bg-rose-500 text-white hover:bg-rose-600'
                                }`}
                            >
                                <span className="material-symbols-outlined text-[18px] font-bold">
                                    {isGroupSelected ? 'check_circle' : 'bolt'}
                                </span>
                                {isGroupSelected ? 'SEÇİLDİ' : 'SEÇ'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (!hasPairings) {
        return (
            <div className="relative">
                {typeof onSwap === 'function' && (
                    <button
                        onClick={handleSwap}
                        className="absolute top-2 left-2 z-50 h-9 w-9 rounded-full bg-white/95 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:text-rose-600 hover:border-rose-300 transition-all shadow-sm flex items-center justify-center"
                        title="Değiştir"
                        aria-label="Değiştir"
                    >
                        <span className="material-symbols-outlined text-[18px]">swap_horiz</span>
                    </button>
                )}
                <RecipeCard
                    recipe={recipe}
                    onCardClick={safeOnCardClick}
                    isSelected={isMainSelected}
                    onToggleSelect={safeOnToggleSelect}
                    hideActionButtons={hideActionButtons}
                    isModern={isModern}
                    isDarkMode={isDarkMode}
                    userPortion={userPortion}
                    highlightAdaptive={highlightAdaptive}
                    {...props}
                />
            </div>
        );
    }

    // Classic / Fallback Group View
    return (
        <div className={`group relative ${completionContainerClass}`}>
            <div className="flex items-center justify-between mb-4 px-2">
                <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-rose-500 text-[18px] font-black animate-pulse">auto_awesome</span>
                    <span className="text-[11px] font-black tracking-[0.2em] uppercase text-rose-600 dark:text-rose-400">
                        BİRLİKTE YAKIŞAN LEZZETLER
                    </span>
                </div>

                {safeOnToggleSelect && (
                    <button
                        onClick={handleToggleGroup}
                        className={`px-4 py-2 text-[10px] font-black rounded-2xl transition-all shadow-xl flex items-center gap-2 hover:scale-105 active:scale-95 border ${isGroupSelected
                            ? 'bg-rose-500 text-white border-rose-400 shadow-rose-500/20'
                            : 'bg-white dark:bg-slate-800 text-rose-500 border-rose-100 dark:border-rose-900/30'
                            }`}
                    >
                        <span className="material-symbols-outlined text-[16px] font-black">
                            {isGroupSelected ? 'check_circle' : 'restaurant_menu'}
                        </span>
                        {isGroupSelected ? 'SEÇİMİ KALDIR' : 'MENÜYÜ SEÇ'}
                    </button>
                )}
            </div>

            <div className={`border-2 rounded-[3rem] p-6 relative overflow-hidden transition-all duration-500 ${isGroupSelected
                ? 'border-emerald-500 bg-emerald-50/5 dark:bg-emerald-950/5 shadow-emerald-500/10'
                : 'border-rose-200 dark:border-rose-900/30 bg-gradient-to-br from-rose-50/20 to-white dark:from-rose-950/5 dark:to-slate-900 shadow-2xl shadow-rose-500/5'
                }`}>
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-rose-500/5 rounded-full blur-3xl pointer-events-none"></div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
                    <div className="flex flex-col h-full">
                        <div className="mb-3 px-3">
                            <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest bg-slate-100 dark:bg-white/5 px-2 py-0.5 rounded-lg">
                                ANA YEMEK
                            </span>
                        </div>
                        <div className="flex-1 h-full">
                            <RecipeCard
                                recipe={recipe}
                                onCardClick={safeOnCardClick}
                                isSelected={isMainSelected}
                                onToggleSelect={safeOnToggleSelect}
                                hideActionButtons={hideActionButtons}
                                isCompact={true}
                                isModern={isModern}
                                isDarkMode={isDarkMode}
                                userPortion={userPortion}
                                highlightAdaptive={highlightAdaptive}
                                {...props}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-6">
                        {recipe.suggestedPairings.map((pairing, pIdx) => (
                            <div key={pIdx} className="flex flex-col h-full border-t md:border-t-0 md:border-l border-slate-100 dark:border-white/5 md:pl-6 pt-6 md:pt-0">
                                <div className="mb-3 px-3 flex items-center justify-between">
                                    <span className="text-[10px] font-black text-rose-500 uppercase tracking-widest bg-rose-50 dark:bg-rose-900/20 px-2 py-0.5 rounded-lg">
                                        EŞLİKÇİ
                                    </span>
                                </div>
                                <div className="flex-1 h-full">
                                    <RecipeCard
                                        recipe={pairing}
                                        onCardClick={safeOnCardClick}
                                        isSelected={selectedRecipeNames.includes(pairing.name || pairing.title)}
                                        onToggleSelect={safeOnToggleSelect}
                                        hideActionButtons={hideActionButtons}
                                        isCompact={true}
                                        isModern={isModern}
                                        isDarkMode={isDarkMode}
                                        userPortion={userPortion}
                                        highlightAdaptive={highlightAdaptive}
                                        {...props}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecipeMenuGroup;
